import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { colors } from '../../styles/colors';
import {
  getOfflinePendingSummary,
  getOfflinePendingOps,
  requestOfflineFlush,
  purgeOfflineNoiseOps
} from '../../services/api';

const Fab = styled.button`
  position: fixed;
  right: 18px;
  bottom: 88px;
  z-index: 1200;
  min-width: 48px;
  min-height: 48px;
  padding: 10px 14px;
  border: none;
  border-radius: 999px;
  background: ${({ $online }) => ($online ? colors.primary : 'rgb(230, 126, 34)')};
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  touch-action: manipulation;

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }

  @media (max-width: 768px) {
    right: 12px;
    bottom: 78px;
  }
`;

const Panel = styled.div`
  position: fixed;
  right: 18px;
  bottom: 148px;
  z-index: 1201;
  width: min(360px, calc(100vw - 24px));
  max-height: min(70vh, 520px);
  overflow: auto;
  background: var(--ucp-bg-card, #fff);
  color: var(--ucp-text, #222);
  border: 1px solid var(--ucp-border, rgba(0, 0, 0, 0.12));
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
  padding: 12px 14px;
`;

const Title = styled.div`
  font-weight: 700;
  margin-bottom: 6px;
`;

const Line = styled.div`
  font-size: 13px;
  color: var(--ucp-text-muted, #555);
  margin: 4px 0;
`;

const OpList = styled.ul`
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
  max-height: 220px;
  overflow: auto;
  border-top: 1px solid var(--ucp-border, rgba(0, 0, 0, 0.08));
`;

const OpItem = styled.li`
  font-size: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--ucp-border, rgba(0, 0, 0, 0.06));
  word-break: break-all;
`;

const OpMeta = styled.div`
  color: var(--ucp-text-muted, #666);
  margin-top: 2px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  flex: 1;
  min-width: 90px;
  border: none;
  border-radius: 8px;
  padding: 8px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $primary, $danger }) =>
    $danger ? 'rgb(192, 57, 43)' : $primary ? colors.primary : 'rgba(0,0,0,0.08)'};
  color: ${({ $primary, $danger }) => ($primary || $danger ? '#fff' : 'inherit')};
`;

function shortenUrl(url) {
  const raw = String(url || '');
  if (raw.length <= 64) return raw;
  return `${raw.slice(0, 28)}…${raw.slice(-28)}`;
}

/**
 * Indicateur sync offline — tous rôles / AppLayout (GO_OFFLINE_FULL W1 + W2).
 */
function OfflineSyncStatus() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const [pending, setPending] = useState(0);
  const [ops, setOps] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const summary = await getOfflinePendingSummary();
      setPending(Number(summary?.pending) || 0);
      if (open) {
        const list = await getOfflinePendingOps({ limit: 30 });
        setOps(Array.isArray(list) ? list : []);
      }
    } catch {
      setPending(0);
      setOps([]);
    }
  }, [open]);

  useEffect(() => {
    void purgeOfflineNoiseOps().finally(() => void refresh());
    const onOnline = () => {
      setOnline(true);
      void refresh();
    };
    const onOffline = () => {
      setOnline(false);
      void refresh();
    };
    const onQueued = () => void refresh();
    const onSynced = () => void refresh();
    const onDropped = () => void refresh();

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('ucp-offline-queued', onQueued);
    window.addEventListener('ucp-offline-sync-success', onSynced);
    window.addEventListener('ucp-offline-sync-dropped', onDropped);
    const interval = setInterval(() => void refresh(), 15000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('ucp-offline-queued', onQueued);
      window.removeEventListener('ucp-offline-sync-success', onSynced);
      window.removeEventListener('ucp-offline-sync-dropped', onDropped);
      clearInterval(interval);
    };
  }, [refresh]);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  if (online && pending <= 0) return null;

  const label = !online
    ? t('offlineSync.offlineBadge', { count: pending })
    : t('offlineSync.pendingBadge', { count: pending });

  return (
    <>
      <Fab
        type="button"
        $online={online}
        title={label}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{online ? '🔄' : '📡'}</span>
        <span>{pending > 0 ? pending : '!'}</span>
      </Fab>
      {open && (
        <Panel role="dialog" aria-label={t('offlineSync.panelTitle')}>
          <Title>{t('offlineSync.panelTitle')}</Title>
          <Line>
            {online ? t('offlineSync.statusOnline') : t('offlineSync.statusOffline')}
          </Line>
          <Line>{t('offlineSync.pendingLine', { count: pending })}</Line>
          <Line>{t('offlineSync.help')}</Line>
          {ops.length > 0 && (
            <OpList>
              {ops.map((op) => (
                <OpItem key={op.clientOpId}>
                  <strong>{String(op.method || 'post').toUpperCase()}</strong>{' '}
                  {shortenUrl(op.url)}
                  {op.lastError ? <OpMeta>{op.lastError}</OpMeta> : null}
                </OpItem>
              ))}
            </OpList>
          )}
          <Actions>
            <Btn type="button" onClick={() => setOpen(false)}>
              {t('common.close')}
            </Btn>
            <Btn
              type="button"
              $danger
              disabled={syncing || pending <= 0}
              onClick={async () => {
                setSyncing(true);
                try {
                  await purgeOfflineNoiseOps();
                  await refresh();
                } finally {
                  setSyncing(false);
                }
              }}
            >
              {t('offlineSync.purgeNoise')}
            </Btn>
            <Btn
              type="button"
              $primary
              disabled={!online || pending <= 0 || syncing}
              onClick={async () => {
                setSyncing(true);
                try {
                  await requestOfflineFlush({ force: true });
                  await refresh();
                } finally {
                  setSyncing(false);
                }
              }}
            >
              {syncing ? t('offlineSync.syncing') : t('offlineSync.syncNow')}
            </Btn>
          </Actions>
        </Panel>
      )}
    </>
  );
}

export default OfflineSyncStatus;
